// src/pages/admin/IntercompanyJournalEntryPreview.jsx
import { useMemo } from "react";
import {
  PreviewField,
  PreviewInput,
  fmtLineAmount,
  computeLineBalance,
  useJournalEntryLineLookups,
} from "@common";

/**
 * Read-only preview shown in the confirmation modal before sending an
 * Intercompany Journal Entry to NetSuite.
 *
 * Props:
 *   preview = { payload, monthLabel, totalDraw }
 *   payload = {
 *     memo, subsidiary:{id}, trandate,
 *     line:{ items:[{ lineSubsidiary:{id}, account:{id}, debit|credit, memo }] }
 *   }
 *   onChange?: optional (patch) => void   — used to edit trandate / memo
 */
export default function IntercompanyJournalEntryPreview({ preview, onChange }) {
  const items = useMemo(() => preview?.payload?.line?.items ?? [], [preview]);

  const { accountNameById, subsidiaryNameById, accountsQuery, subsidiariesQuery } =
    useJournalEntryLineLookups(preview?.payload);

  if (!preview) return null;
  const { payload, monthLabel, totalDraw } = preview;

  const { totalDebit, totalCredit, balanced } = computeLineBalance(items);

  const headerSubName =
    subsidiaryNameById[String(payload?.subsidiary?.id)] ||
    (subsidiariesQuery.isLoading ? "Loading…" : payload?.subsidiary?.id || "—");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-palette-gray-divider bg-palette-gray-soft p-3">
        <PreviewField label="Header Subsidiary" value={headerSubName} />
        <PreviewField label="Month" value={monthLabel || "—"} />
        {onChange ? (
          <PreviewField label="Transaction Date">
            <PreviewInput
              type="date"
              value={payload?.trandate ?? ""}
              onChange={(e) => onChange({ trandate: e.target.value })}
              className="text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
            />
          </PreviewField>
        ) : (
          <PreviewField label="Transaction Date" value={payload?.trandate} />
        )}
        <PreviewField label="Total DDS Draw" value={fmtLineAmount(totalDraw ?? 0)} />
        <div className="sm:col-span-2">
          {onChange ? (
            <PreviewField label="Memo">
              <PreviewInput
                type="text"
                value={payload?.memo ?? ""}
                onChange={(e) => onChange({ memo: e.target.value })}
                className="w-full text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
              />
            </PreviewField>
          ) : (
            <PreviewField label="Memo" value={payload?.memo} />
          )}
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-semibold text-palette-gray-heading">
            Line Items ({items.length})
          </span>
          <span
            className={`text-xs font-semibold ${balanced ? "text-palette-emerald-hover" : "text-palette-red-heading"}`}
          >
            {balanced ? "Balanced" : "Not balanced"}
          </span>
        </div>
        <div className="max-h-[28rem] overflow-y-auto rounded-md border border-palette-gray-divider">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-palette-gray-subtle text-palette-gray-heading">
              <tr>
                <th className="text-left px-3 py-3 font-semibold">Subsidiary</th>
                <th className="text-left px-3 py-3 font-semibold">Account</th>
                <th className="text-right px-3 py-3 font-semibold">Debit</th>
                <th className="text-right px-3 py-3 font-semibold">Credit</th>
                <th className="text-left px-3 py-3 font-semibold">Memo</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-palette-gray-secondary">
                    No line items.
                  </td>
                </tr>
              ) : (
                items.map((it, idx) => {
                  const sid = it?.lineSubsidiary?.id;
                  const aid = it?.account?.id;
                  const subName = sid != null ? subsidiaryNameById[String(sid)] : "";
                  const acctName = aid != null ? accountNameById[String(aid)] : "";
                  const isDebit = Number(it.debit) > 0;
                  const isCredit = Number(it.credit) > 0;
                  const rowCls = isDebit
                    ? "bg-palette-emerald-soft hover:bg-palette-emerald-muted"
                    : isCredit
                      ? "bg-palette-rose-soft hover:bg-palette-rose-muted"
                      : "";
                  return (
                    <tr
                      key={idx}
                      className={`border-t border-palette-gray-subtle align-top ${rowCls}`}
                    >
                      <td className="px-3 py-3">
                        {subName ? (
                          <span className="text-palette-gray-text">{subName}</span>
                        ) : subsidiariesQuery.isLoading ? (
                          <span className="text-palette-gray-placeholder italic text-xs">
                            Loading…
                          </span>
                        ) : (
                          <span className="font-mono text-xs text-palette-gray-secondary">
                            {sid ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {acctName ? (
                          <span className="text-palette-gray-text">{acctName}</span>
                        ) : accountsQuery.isLoading ? (
                          <span className="text-palette-gray-placeholder italic text-xs">
                            Loading…
                          </span>
                        ) : (
                          <span className="font-mono text-xs text-palette-gray-secondary">
                            {aid ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {it.debit != null ? fmtLineAmount(it.debit) : ""}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {it.credit != null ? fmtLineAmount(it.credit) : ""}
                      </td>
                      <td className="px-3 py-3 text-palette-gray-heading max-w-xs break-words">
                        {it.memo || (
                          <span className="text-palette-gray-placeholder italic text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot className="bg-palette-gray-soft">
              <tr className="border-t border-palette-gray-divider">
                <td colSpan={2} className="px-3 py-3 text-right font-semibold">
                  Totals
                </td>
                <td className="px-3 py-3 text-right font-semibold">{fmtLineAmount(totalDebit)}</td>
                <td className="px-3 py-3 text-right font-semibold">{fmtLineAmount(totalCredit)}</td>
                <td className="px-3 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <p className="text-xs text-palette-gray-secondary">
        This will create a new Intercompany Journal Entry in NetSuite. Please review the values
        above before confirming.
      </p>
    </div>
  );
}
